import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import '../Css/AboutUS.css';

export default function AboutUs() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elementsToAnimate = document.querySelectorAll('.about-us-page .fade-up, .about-us-page .reveal');
    elementsToAnimate.forEach((el) => {
      observer.observe(el);
    });

    // Scroll to top on mount
    window.scrollTo(0, 0);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="about-us-page">
      <Helmet>
        <title>About OOK – Engineering Tools for Modern Solutions</title>
        <meta
          name="description"
          content="Discover OOK's mission to revolutionize engineering and education. We provide high-precision structural calculators and advanced AI writing tools designed for professionals, researchers, and students worldwide."
        />
        <link rel="canonical" href="https://www.ookcalculator.com/AboutUs" />
      </Helmet>
      <br />
      {/* HERO SECTION */}
      <section className="hero">
        <div>
          <div className="hero-eyebrow fade-up">About OOK Calculator</div>
          <h1 className="fade-up delay-1 ">
            Engineering Tools<br />
            <em>Built</em> for Precision
          </h1>
          <p className="hero-desc fade-up delay-2">
            We build reliable, precision-engineered calculators and AI-powered writing solutions, and technical resources designed to deliver accurate results for engineers, designers, students, and professionals worldwide.
          </p>
          <div className="hero-actions fade-up delay-3">
            <Link to="/" className="btn-primary">
              Explore Tools
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
            <a href="#team" className="btn-secondary">
              Meet the team
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" />
                <path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
            </a>
          </div>
        </div>

        <div className="hero-right fade-up delay-4">
          <div className="stat-card dark-theme">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" />
                <path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
            </div>
            <div className="stat-num">10K+</div>
            <div className="stat-label">Engineers Served</div>
          </div>
          {/* <div className="stat-card">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div className="stat-num">20+</div>
            <div className="stat-label">Calculators Built</div>
          </div> */}
          <div className="stat-card">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <path d="M8 21h8M12 17v4" />
              </svg>
            </div>
            <div className="stat-num">4</div>
            <div className="stat-label">Core Disciplines</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4l3 3" />
              </svg>
            </div>
            <div className="stat-num">99.9%</div>
            <div className="stat-label">Uptime</div>
          </div>
        </div>
      </section>

      <hr className="section-divider" />

      {/* VISION & MISSION */}
      <section className="section">
        <div className="section-tag">Our foundation</div>
        <div className="vm-grid-2">
          <div className="vm-card fade-up">
            <div className="vm-icon blue">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="4" />
                <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
                <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
                <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
                <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
              </svg>
            </div>
            <h3>Our Vision</h3>
            <p>
              To provide groundbreaking and dependable engineering calculators —
              consistently advancing technological frontiers to empower engineers and
              learners across the globe.
            </p>
          </div>
          <div className="vm-card fade-up delay-1">
            <div className="vm-icon teal">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
              </svg>
            </div>
            <h3>Our Mission</h3>
            <p>
              To deliver precise and efficient results that enable engineers and
              students to attain excellence — through innovative solutions that
              enhance productivity and facilitate success.
            </p>
          </div>
        </div>
      </section>

      <hr className="section-divider" />

      {/* VALUES */}
      <section className="section values-section">
        <div className="section-tag">Our Values</div>
        <div className="values-list-layout">
          <div className="values-list-header">
            <h2 className="values-list-heading">What We Stand For</h2>
            <p className="values-list-sub">The principles that guide every tool we build and every decision we make.</p>
          </div>
          <div className="values-list">
            <div className="value-list-item fade-up">
              <div className="value-num-badge">01</div>
              <div className="value-list-body">
                <h4>Accuracy</h4>
                <p>Every calculator is developed using established engineering principles and verified methodologies.</p>
              </div>
            </div>
            <div className="value-list-item fade-up delay-1">
              <div className="value-num-badge">02</div>
              <div className="value-list-body">
                <h4>Accessibility</h4>
                <p>Engineering tools should be easy to use regardless of experience level.</p>
              </div>
            </div>
            <div className="value-list-item fade-up delay-2">
              <div className="value-num-badge">03</div>
              <div className="value-list-body">
                <h4>Continuous Improvement</h4>
                <p>We continuously refine our tools, features, and user experience based on engineering best practices and user feedback.</p>
              </div>
            </div>
            <div className="value-list-item fade-up delay-3">
              <div className="value-num-badge">04</div>
              <div className="value-list-body">
                <h4>Reliability</h4>
                <p>Users should be able to trust the results and performance of the tools they rely on every day.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="section-divider" />

      {/* CAPABILITIES */}
      <section className="section">
        <div className="section-tag">What we do best</div>
        <div className="cap-grid">
          <div className="cap-card fade-up">
            <div className="cap-icon blue">
              <svg viewBox="0 0 24 24">
                <path d="M2 20h20M6 20V10M12 20V4M18 20v-7" />
              </svg>
            </div>
            <h4>Structure Design</h4>
            <p>Structural analysis and design tools for real engineering challenges.</p>
          </div>
          <div className="cap-card fade-up delay-1">
            <div className="cap-icon blue">
              <svg viewBox="0 0 24 24">
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <path d="M9 7h6M9 11h6M9 15h4" />
              </svg>
            </div>
            <h4>Engineering Calculators</h4>
            <p>Fast, accurate calculators built on verified engineering formulas.</p>
          </div>
          <div className="cap-card fade-up delay-2">
            <div className="cap-icon teal">
              <svg viewBox="0 0 24 24">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
            </div>
            <h4>Web Design & Development</h4>
            <p>Custom, responsive web applications built for speed, SEO, and visual excellence.</p>
          </div>
          <div className="cap-card fade-up delay-3">
            <div className="cap-icon teal">
              <svg viewBox="0 0 24 24">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
            </div>
            <h4>Technical Consultancy</h4>
            <p>Expert guidance on structural, mechanical, and engineering projects.</p>
          </div>
        </div>
      </section>

      <hr className="section-divider" />

      {/* PRODUCTS */}
      <section className="section">
        <div className="section-tag">Our tools</div>
        <div className="products-wrap">
          <div>
            <div className="product-group-label pg-calc">
              <span></span>Engineering Calculators
            </div>
            <div className="product-list">
              <Link to="/PadEye" className="product-item fade-up">
                <div className="product-item-dot dot-calc">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </div>
                <div className="product-item-text">
                  <h5>Pad Eye Calculator</h5>
                  <p>Load capacity analysis for lifting & rigging</p>
                </div>
                <span className="product-arrow">→</span>
              </Link>
              <Link to="/BeamProperties" className="product-item fade-up delay-1">
                <div className="product-item-dot dot-calc">
                  <svg viewBox="0 0 24 24">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="product-item-text">
                  <h5>Beam Properties</h5>
                  <p>Section modulus, moment of inertia & more</p>
                </div>
                <span className="product-arrow">→</span>
              </Link>
              <Link to="/BeamDeflection" className="product-item fade-up delay-2">
                <div className="product-item-dot dot-calc">
                  <svg viewBox="0 0 24 24">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                </div>
                <div className="product-item-text">
                  <h5>Beam Deflection</h5>
                  <p>Deflection & bending under load configurations</p>
                </div>
                <span className="product-arrow">→</span>
              </Link>
              <Link to="/NetForce" className="product-item fade-up delay-3">
                <div className="product-item-dot dot-calc">
                  <svg viewBox="0 0 24 24">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
                <div className="product-item-text">
                  <h5>Net Force Calculator</h5>
                  <p>Resolve force vectors to find resultant force</p>
                </div>
                <span className="product-arrow">→</span>
              </Link>
            </div>
          </div>

          <div>
            <div className="product-group-label pg-ai">
              <span></span>AI Writing & Analysis
            </div>
            <div className="product-list">
              <Link to="/AIDetector" className="product-item fade-up">
                <div className="product-item-dot dot-ai">
                  <svg viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>
                <div className="product-item-text">
                  <h5>AI Content Detector</h5>
                  <p>Identify AI-generated text with precision</p>
                </div>
                <span className="product-arrow">→</span>
              </Link>
              <Link to="/Humanizer" className="product-item fade-up delay-1">
                <div className="product-item-dot dot-ai">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                  </svg>
                </div>
                <div className="product-item-text">
                  <h5>AI Text Humanizer</h5>
                  <p>Transform AI writing into natural prose</p>
                </div>
                <span className="product-arrow">→</span>
              </Link>
              <Link to="/Paraphraser" className="product-item fade-up delay-2">
                <div className="product-item-dot dot-ai">
                  <svg viewBox="0 0 24 24">
                    <path d="M4 6h16" />
                    <path d="M4 12h16" />
                    <path d="M4 18h7" />
                  </svg>
                </div>
                <div className="product-item-text">
                  <h5>Paraphraser & Summarizer</h5>
                  <p>Reword and condense content clearly</p>
                </div>
                <span className="product-arrow">→</span>
              </Link>
              <Link to="/Plagiarism" className="product-item fade-up delay-3">
                <div className="product-item-dot dot-ai">
                  <svg viewBox="0 0 24 24">
                    <path d="M9 11l3 3L22 4" />
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                  </svg>
                </div>
                <div className="product-item-text">
                  <h5>Plagiarism & Grammar Checker</h5>
                  <p>Ensure originality and correctness</p>
                </div>
                <span className="product-arrow">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <hr className="section-divider" />

      {/* TEAM */}
      <section className="section" id="team">
        <div className="section-tag">Our team</div>
        <div className="team-grid">
          <div className="team-card fade-up">
            <div className="team-header">
              <div className="team-avatar av-blue">DA</div>
              <div className="team-meta">
                <h4>Daman Anand</h4>
                <div className="team-role">Founder</div>
              </div>
            </div>
            <p>
              With a Bachelor's degree in Mechanical & Automation Engineering, he is
              a skilled Design and Structural Engineer at Dimension Consultants Pte Ltd.
              His goal is to utilize research and marketing skills, along with providing
              calculators, to continually foster mutually beneficial relationships between
              the company and its users.
            </p>
            <div className="team-badges">
              <span className="badge">Mechanical Engineering</span>
              <span className="badge">Structural Design</span>
              <span className="badge">Founder</span>
              <span className="badge">Dimension Consultants Pte Ltd</span>
            </div>
          </div>

          <div className="team-card fade-up delay-1">
            <div className="team-header">
              <div className="team-avatar av-teal">HG</div>
              <div className="team-meta">
                <h4>Himanshu Gupta</h4>
                <div className="team-role">Quality Assurance</div>
              </div>
            </div>
            <p>
              Himanshu has a Bachelor's Degree in Mechanical & Automation Engineering, he is
              an experienced Mechanical Designer at Dimension Consultants Pte Ltd. Himanshu's
              responsibilities include result verification and handling technical data. He brings
              exceptional technical skills and a positive attitude to support both our users and
              the development team.
            </p>
            <div className="team-badges">
              <span className="badge">Mechanical Design</span>
              <span className="badge">Quality Assurance</span>
              <span className="badge">Technical Verification</span>
              <span className="badge">Dimension Consultants Pte Ltd</span>
            </div>
          </div>

          <div className="team-card fade-up delay-2">
            <div className="team-header">
              <div className="team-avatar av-blue">M</div>
              <div className="team-meta">
                <h4>Megha</h4>
                <div className="team-role">Product Developer</div>
              </div>
            </div>
            <p>
              With a strong background in programming, she is a versatile individual who
              brings extensive knowledge and experience. Megha is dedicated to the development
              and ongoing maintenance of various OOK Calculators.
            </p>
            <div className="team-badges">
              <span className="badge">Programming</span>
              <span className="badge">Calculator Development</span>
              <span className="badge">Product Developer</span>
              <span className="badge">Maintenance</span>
            </div>
          </div>

          <div className="team-card fade-up delay-3">
            <div className="team-header">
              <div className="team-avatar av-teal">RY</div>
              <div className="team-meta">
                <h4>Rajeev Yadav</h4>
                <div className="team-role">Senior Product Developer</div>
              </div>
            </div>
            <p>
              Rajeev Yadav is proficient in supporting web applications, managing server-side
              operations, and overseeing database management within the OOK Calculator project.
            </p>
            <div className="team-badges">
              <span className="badge">Senior Product Developer</span>
              <span className="badge">Web Applications</span>
              <span className="badge">Server-side Operations</span>
              <span className="badge">Database Management</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
